/**
 * What a day offers when you ask it: the right-click menu, and the menu the
 * date number opens on a plain click.
 *
 * ── WHY A DAY NEEDS A MENU AT ALL ─────────────────────────────────────────
 *
 * A grid cell is about 118px wide and draws four items before it gives up and
 * prints "+3 more". Until now that sentence was a dead end on the wide view:
 * the rest of the day existed, the cell said so, and there was no way to reach
 * them without making the window narrower until the grid became a day list.
 * This menu is that way. It lists everything the day holds, in the same order
 * and with the same colours, and each row goes where the chip would have gone.
 *
 * It also carries the plan action, because a menu that answers "what is on this
 * day" and cannot add to it is half an answer.
 *
 * ── WHY THE DATE NUMBER OPENS IT TOO ──────────────────────────────────────
 *
 * A right-click is not discoverable, it is not offered by every touch browser,
 * and macOS has no keyboard route to it. Every other context menu in this CRM
 * has a three-dot button beside it for exactly that reason. A calendar cell has
 * no room for one, but it already draws a circle with the date in it that did
 * nothing at all - so that circle is the button, and the gesture people try
 * first is the one that works.
 */
import { CalendarPlus } from "lucide-react";
import { cn } from "@wa/ui";
import type { CalendarItem } from "../../../lib/calendar";
import { KIND_DOT, KIND_ICON, itemTitle, type T } from "./kinds";
import { timeLabel } from "./lib";

export function DayMenuItems({
  day,
  items,
  onOpen,
  onPlan,
  close,
  t,
  locale,
}: {
  day: Date;
  /** Every item on the day, not the four the cell had room for. */
  items: CalendarItem[];
  onOpen: (item: CalendarItem) => void;
  onPlan: (day: Date) => void;
  close: () => void;
  t: T;
  locale: string;
}) {
  // Closed BEFORE the action runs, not after. Both actions leave this screen -
  // one navigates, the other opens a dialog over it - and a menu still standing
  // behind a dialog is a menu the operator has to dismiss twice.
  const act = (fn: () => void) => {
    close();
    fn();
  };

  return (
    <>
      <div className="crm-menu-head">
        <div className="crm-menu-t">{longDate(day, locale)}</div>
        {/* Only when the day is empty. A count would need a plural entry in both
            catalogues to say "1 deadline" correctly, and the rows below already
            answer "how many" by being there. */}
        {items.length === 0 ? <div className="crm-menu-s">{t("calendar.dayNothing")}</div> : null}
      </div>

      <button type="button" className="crm-mi" onClick={() => act(() => onPlan(day))}>
        <CalendarPlus className="size-4" />
        {t("calendar.plan")}
      </button>

      {items.length > 0 ? <div className="crm-menu-sep" /> : null}

      {items.map((item) => {
        const Icon = KIND_ICON[item.kind];
        const at = timeLabel(new Date(item.at), locale);
        const title = itemTitle(item, t);
        return (
          <button
            key={item.key}
            type="button"
            className="crm-mi"
            // An item with nowhere to go is still worth listing - it is part of
            // the day - but it is not a row you press.
            disabled={!item.to}
            onClick={() => act(() => onOpen(item))}
          >
            <span className={cn("size-2 shrink-0 rounded-full", KIND_DOT[item.kind])} />
            <Icon className="size-4" />
            {/* Bounded, or `truncate` never fires: `.crm-menu` has a minimum
                width and no maximum, so a flex child free to grow makes the
                PANEL wider instead of shortening the line inside it. A project
                called "Metsatare uus koduleht, teine etapp" would have opened a
                600px menu at the pointer. */}
            <span className="min-w-0 max-w-[260px] flex-1">
              <span className={cn("block truncate", item.done && "line-through opacity-60")}>{title}</span>
              {/* The clock and the client on one quiet second line. The menu has
                  the width the cell never had, so nothing here has to choose
                  between telling you when and telling you who. */}
              {at || item.sub ? (
                <span className="crm-mi-sub block truncate">{[at, item.sub].filter(Boolean).join(" - ")}</span>
              ) : null}
            </span>
          </button>
        );
      })}
    </>
  );
}

/** "Thursday, 27 August 2026". Falls back to the machine form where the locale
 *  is unknown, the same way the rest of this section does. */
function longDate(day: Date, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(day);
  } catch {
    return day.toDateString();
  }
}
